// Constantes pour les chiffrages
const FIGURES = [
  { value: '5', label: 'Quinte', display: '5' },
  { value: '6', label: 'Sixte', display: '6' },
  { value: '64', label: 'Quarte-sixte', display: '6/4', isStacked: true, displayArray: ['6', '4'] },
  { value: '7', label: 'Septième', display: '7' },
  { value: '65', label: 'Sixte-quinte', display: '6/5', isStacked: true, displayArray: ['6', '5'] },
  { value: '43', label: 'Quarte-tierce', display: '4/3', isStacked: true, displayArray: ['4', '3'] },
  { value: '2', label: 'Seconde', display: '2' },
  { value: '9', label: 'Neuvième', display: '9' },
  { value: '11', label: 'Onzième', display: '11' },
  { value: '13', label: 'Treizième', display: '13' },
  { value: '54', label: 'Quinte-quarte', display: '5/4', isStacked: true, displayArray: ['5', '4'] }
]

/**
 * Formate un accord avec tous les détails pour l'affichage
 * @param {Object} chord - Objet accord avec degree, accidental, quality, figure, etc.
 * @param {Array} figures - Tableau des chiffrages (optionnel, utilise FIGURES par défaut)
 * @returns {string} - Accord formaté (ex: "♭II6", "V7", "(IV)6/4")
 */
export function formatChordDetailed(chord, figures = FIGURES) {
  if (!chord) return 'Non répondu'
  
  const parts = []
  const showParens = chord.isBorrowed && !chord.borrowedFromDegree

  // Emprunt (parenthèses uniquement si pas d'emprunt slash)
  if (showParens) parts.push('(')
  
  // Altération
  if (chord.accidental) {
    parts.push(chord.accidental === 'b' ? '♭' : chord.accidental === '#' ? '♯' : '♮')
  }
  
  // Racine spéciale ou degré
  if (chord.specialRoot) {
    const special = { 'N': 'II♭6', 'It': 'It+6', 'Fr': 'Fr+6', 'Gr': 'Gr+6' }[chord.specialRoot]
    parts.push(special || chord.specialRoot)
  } else if (chord.degree && chord.figure === '64' && chord.sixFourVariant === 'cadential') {
    // 64 de cadence : chiffrage baroque "Cad." + 6/4 superposés
    parts.push('Cad.')
    parts.push('6/4')
  } else if (chord.degree && chord.figure === '64' && chord.sixFourVariant === 'passing') {
    // 64 de passage entre I et I6 : on note V64
    parts.push('V')
    parts.push('6/4')
  } else if (chord.degree) {
    parts.push(chord.degree)
    // Qualité
    if (chord.quality) parts.push(chord.quality)
  } else if (chord.selectedFunction) {
    // Fonction seule
    parts.push(chord.selectedFunction)
  }
  
  // Chiffrage (sauf si déjà ajouté pour cad64 / V64)
  if (chord.figure && chord.figure !== '5') {
    if (chord.degree && chord.figure === '64' && (chord.sixFourVariant === 'cadential' || chord.sixFourVariant === 'passing')) {
      // Déjà ajouté ci-dessus
    } else {
      const figObj = figures.find(f => f.value === chord.figure)
      parts.push(figObj?.display || chord.figure)
    }
  }
  
  // Fermeture emprunt
  if (showParens) parts.push(')')

  // Emprunt non résolu (slash, ex. IV/V)
  if (chord.borrowedFromDegree) {
    parts.push(' / ')
    parts.push(chord.borrowedFromDegree)
  }
  
  // Note pédale (accord/basse, ex. II/I)
  if (chord.pedalDegree) {
    parts.push(' / ')
    parts.push(chord.pedalDegree)
  }
  
  return parts.join('') || 'Non répondu'
}

/**
 * Retourne le libellé affichable d'une réponse utilisateur sauvegardée.
 * Gère : chaînes, réponses QCM { qcmAnswer, function? }, mode fonctions seules { selectedFunction?, function? }, objets accord complets.
 * @param {string|object|null} answer - Réponse (string, ou objet avec qcmAnswer / selectedFunction / function / chord)
 * @returns {string}
 */
export function userAnswerToLabel(answer) {
  if (answer == null) return 'Non répondu'
  if (typeof answer === 'string') return answer
  if (typeof answer === 'object') {
    if (answer.qcmAnswer != null) return String(answer.qcmAnswer).trim()
    if (answer.selectedFunction != null || answer.function != null) return String(answer.selectedFunction ?? answer.function).trim()
  }
  return formatChordDetailed(answer)
}

/**
 * Le slash « X / Y » avec Y = degré romain sert soit à la pédale (IV/I), soit à la dominante secondaire (V7/V).
 * On supprime la fausse pédale quand la partie gauche est clairement une dominante (V7, V65, …) ou V/x avec x ≠ I.
 */
function shouldSuppressFalsePedalFromSlashNotation(baseStr, pedalRoman) {
  if (!baseStr || !pedalRoman) return false
  const ped = String(pedalRoman).trim().toUpperCase()
  const b = baseStr.trim()
  const acc = '(?:[♭#♯b]\\s*)?'
  const vFigures = '(?:(?:6\\/5|6\\/4|4\\/3|5\\/4)|64|65|43|56|2|7|6|9|11|13|\\d{2})'
  // Exiger au moins un chiffrage après V (V7, V65, …) — pas « V » seul, sinon on confond avec V/I en pédale.
  const hasDominantChordFigures = new RegExp(`^${acc}V(?:°|\\+)?(?:${vFigures})$`, 'i').test(b)
  const bareV = new RegExp(`^${acc}V(?:°|\\+)?$`, 'i').test(b)
  // V7/V = emprunt ; V7/I = pédale sur I (ne pas supprimer quand la basse est I).
  if (hasDominantChordFigures && ped !== 'I') return true
  if (bareV && ped !== 'I') return true
  return false
}

const TRAILING_ROMAN_SLASH_RE = /^(.+)\s*\/\s*(I|II|III|IV|V|VI|VII)\s*$/i

const EMPTY_PARSED_CHORD = { leading: '', figure: null, pedalDegree: null, borrowedFromDegree: null }

/** Dernier suffixe = pédale ; avant-dernier = emprunt (aligné sur formatChordString / exerciseMarkerValidation). */
function peelTrailingRomanSuffixes(str) {
  let rest = str.trim()
  const suffixes = []
  for (let i = 0; i < 8; i++) {
    const m = rest.match(TRAILING_ROMAN_SLASH_RE)
    if (!m) break
    suffixes.push(m[2].toUpperCase())
    rest = m[1].trim()
  }
  return { core: rest, suffixes }
}

/** Un seul suffixe ambigu : dominante secondaire → emprunt ; I seul → pédale ; sinon emprunt. */
function assignBorrowedAndPedalFromSuffixes(suffixes, core) {
  if (suffixes.length >= 2) {
    return { pedalDegree: suffixes[0], borrowedFromDegree: suffixes[1] }
  }
  if (suffixes.length === 1) {
    const r = suffixes[0]
    if (shouldSuppressFalsePedalFromSlashNotation(core, r)) {
      return { pedalDegree: null, borrowedFromDegree: r }
    }
    if (r === 'I') {
      return { pedalDegree: r, borrowedFromDegree: null }
    }
    return { pedalDegree: null, borrowedFromDegree: r }
  }
  return { pedalDegree: null, borrowedFromDegree: null }
}

function parseCoreDisplayString(baseStr, suffixFields) {
  const { pedalDegree, borrowedFromDegree } = suffixFields
  const withSuffixes = (leading, figure) => ({ leading, figure, pedalDegree, borrowedFromDegree })

  if (baseStr === 'It+6' || baseStr === 'Fr+6' || baseStr === 'Gr+6') {
    return withSuffixes(baseStr, null)
  }

  if (baseStr === 'II♭6' || baseStr === 'IIb6') {
    return withSuffixes('II', { stacked: false, value: '♭6' })
  }

  const stackedMatch = baseStr.match(/(6\/4|6\/5|4\/3|5\/4)$/)
  if (stackedMatch) {
    const frac = stackedMatch[1]
    let leading = baseStr.slice(0, -frac.length).trim()
    if (frac === '6/4') {
      const n = leading.trim()
      if (n === 'cad' || n === 'Cad.' || /^Cad\.\s*$/i.test(n)) leading = 'Cad.'
    }
    const digits = frac.split('/')
    return withSuffixes(leading, { stacked: true, digits })
  }

  const singleMatch = baseStr.match(/^(.+?)(\d{1,2})$/)
  if (singleMatch) {
    return withSuffixes(singleMatch[1], { stacked: false, value: singleMatch[2] })
  }

  return withSuffixes(baseStr, null)
}

/**
 * Parse une chaîne d'affichage d'accord (ex. "V6/5", "I6/4", "cad6/4", "II♭6", "V7", "V7 / V / I")
 * pour permettre un rendu baroque (chiffres superposés) et distinguer emprunt (slash) vs pédale (fraction).
 * @param {string} str - Chaîne formatée (sortie de formatChordString / équivalent)
 * @returns {{ leading: string, figure: null | { stacked: true, digits: string[] } | { stacked: false, value: string }, pedalDegree: string|null, borrowedFromDegree: string|null }}
 */
export function parseChordDisplayString(str) {
  if (!str || typeof str !== 'string') return { ...EMPTY_PARSED_CHORD }
  const s = str.trim()
  if (!s) return { ...EMPTY_PARSED_CHORD }

  const { core, suffixes } = peelTrailingRomanSuffixes(s)
  const suffixFields = assignBorrowedAndPedalFromSuffixes(suffixes, core)
  return parseCoreDisplayString(core, suffixFields)
}

/**
 * Convertit la partie « figure » renvoyée par parseChordDisplayString en valeur interne (64, 65, 7, …).
 * @param {null | { stacked?: boolean, digits?: string[], value?: string }} parsedFigure
 * @returns {string|null}
 */
export function getFigureValueFromParsedFigure(parsedFigure) {
  if (!parsedFigure) return null
  if (parsedFigure.stacked && Array.isArray(parsedFigure.digits)) {
    const joined = parsedFigure.digits.join('/')
    if (joined === '6/4') return '64'
    if (joined === '6/5') return '65'
    if (joined === '4/3') return '43'
    if (joined === '5/4') return '54'
    return parsedFigure.digits[0] || null
  }
  return parsedFigure.value || null
}

import { getDegreeLabelForMode } from './modalScales'

/**
 * Retourne le libellé d'affichage d'un degré selon le mode (generic / major / minor / ionian / …).
 * Aligné sur ChordDisplay (visualiseur mode élève) : majuscule = majeur, minuscule = mineur.
 * @param {string} degree - Degré romain (I, II, III, …)
 * @param {string} degreeMode - 'generic' | 'major' | 'minor' | 'ionian' | 'dorian' | …
 * @returns {string}
 */
export function getDegreeDisplayLabel(degree, degreeMode = 'generic') {
  return getDegreeLabelForMode(degree, degreeMode)
}

export { FIGURES }
